import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  // Observables
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result: BreakpointState) => result.matches),
      shareReplay()
    );

  // Properties
  sidebarVisible: boolean = false;
  personId: string = '';
  personName: string = '';
  isLoggedIn: boolean = false;
  showApplicant: boolean = false;
  isScrolled: boolean = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    public location: Location,
    private element: ElementRef,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.initializeAuthState();
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Initialize authentication state from storage
   */
  private initializeAuthState(): void {
    const token = localStorage.getItem('token');
    const storedPersonId = localStorage.getItem('personId');
    const storedPersonName = localStorage.getItem('personName');
    const userRole = localStorage.getItem('userRole');

    this.isLoggedIn = !!(token || storedPersonId);
    this.personId = storedPersonId || '';
    this.personName = storedPersonName || '';
    this.showApplicant = userRole === 'applicant';
  }

  /**
   * Setup scroll event listener for header styling
   */
  private setupScrollListener(): void {
    // Initial check
    this.isScrolled = window.scrollY > 50;
  }

  /**
   * Handle window scroll event
   */
  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 50;
  }

  /**
   * Open mobile sidebar
   */
  sidebarOpen(): void {
    this.sidebarVisible = true;
    this.renderer.addClass(document.body, 'sidebar-open');
    // Prevent body scroll when sidebar is open
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  /**
   * Close mobile sidebar
   */
  sidebarClose(): void {
    this.sidebarVisible = false;
    this.renderer.removeClass(document.body, 'sidebar-open');
    // Restore body scroll
    this.renderer.removeStyle(document.body, 'overflow');
  }

  /**
   * Toggle mobile sidebar
   */
  sidebarToggle(): void {
    if (this.sidebarVisible) {
      this.sidebarClose();
    } else {
      this.sidebarOpen();
    }
  }

  /**
   * Clear cache and reset state
   */
  private clearCache(): void {
    // Clear all relevant localStorage items
    const itemsToRemove = [
      'token',
      'personId',
      'personName',
      'userRole',
      // Add other relevant items here
    ];

    itemsToRemove.forEach(item => {
      localStorage.removeItem(item);
    });

    // Reset component state
    this.personName = '';
    this.personId = '';
    this.isLoggedIn = false;
    this.showApplicant = false;
  }

  /**
   * Handle user logout
   */
  logout(): void {
    this.clearCache();
    this.sidebarClose(); // Close sidebar if open
    this.router.navigateByUrl('/logout');
  }

  /**
   * Handle escape key to close sidebar
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.sidebarVisible) {
      this.sidebarClose();
    }
  }

  /**
   * Navigate to section (smooth scroll)
   */
  navigateToSection(sectionId: string): void {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.sidebarClose();
    }
  }
}
